import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AudioElement,
    type BaseMultiPageModel,
    type ControllableElement,
    ImageElement,
    VideoElement,
} from '@bynder-studio/render-core';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import { setSaveStatus } from 'packages/store/creativeEditor/creativeEditor.actions';
import { type Ordering } from 'packages/services/graphql/types';
import { useGetCollections } from './useGetCollections';
import { useInfinityLoading } from './useInfinityLoading';
import { type Collection } from 'packages/services/assetSource/fragments';
import useDebouncedState from 'packages/hooks/useDebouncedState';
import { useGetCollectionById } from './useGetCollectionById';

const orderBy: Ordering = {
    direction: 'DESC',
    field: 'CREATED_AT',
};

export const useAssetSourceMultiple = (creativeModel: BaseMultiPageModel, selectedElements: ControllableElement[]) => {
    const dispatch = useThunkDispatch();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [collectionData, setCollectionData] = useState<Collection | null>(null);
    const {
        state: searchTerm,
        debouncedState: debouncedSearchTerm,
        setState: setSearchTerm,
    } = useDebouncedState('', 500);

    const [isCollectionInvalid, setIsCollectionInvalid] = useState(false);
    const [isMixedState, setIsMixedState] = useState(false);
    const { collections, isLoading, loadMore, hasNextPage } = useGetCollections(debouncedSearchTerm, orderBy);
    const { fetchCollectionById } = useGetCollectionById();

    useInfinityLoading({ containerRef, isOpen, isLoading, loadMore, hasNextPage });

    const allLocked = selectedElements.every((el) => el.locked);
    const unlockedElements = selectedElements.filter((el) => !el.locked);

    const collectionIds = useMemo(
        () =>
            unlockedElements
                .map((el) => {
                    const element = el as ImageElement | VideoElement | AudioElement;

                    return element.virtualData?.bynderCollectionId || null;
                })
                .filter(Boolean),
        [selectedElements],
    );

    const uniqueCollectionIds = useMemo(() => [...new Set(collectionIds)], [collectionIds]);

    const commonCollectionId = useMemo(() => {
        if (uniqueCollectionIds.length !== 1) {
            return null;
        }

        const collectionId = uniqueCollectionIds[0];

        if (
            selectedElements.some(
                (el) =>
                    (el as ImageElement | VideoElement | AudioElement).virtualData?.bynderCollectionId !== collectionId,
            )
        ) {
            return null;
        }

        return collectionId;
    }, [uniqueCollectionIds]);

    const handleRemoveClick = useCallback(() => {
        dispatch(setSaveStatus());
        setCollectionData(null);
        setIsMixedState(false);
        setIsCollectionInvalid(false);
        selectedElements.forEach((element) => {
            if (!element.locked) {
                const el = element as ImageElement | VideoElement;
                creativeModel.updateElement(el.id, {
                    virtualData: { ...el.virtualData, bynderCollectionId: null },
                });
            }
        });
    }, [creativeModel, dispatch, selectedElements]);

    const handleClick = useCallback(
        (item: Collection) => {
            dispatch(setSaveStatus());
            setCollectionData(item);
            setIsMixedState(false);
            setIsCollectionInvalid(false);
            unlockedElements.forEach((element) => {
                const el = element as ImageElement | VideoElement;
                creativeModel.updateElement(el.id, {
                    virtualData: { ...el.virtualData, bynderCollectionId: item.id },
                });
            });
        },
        [creativeModel, dispatch, selectedElements],
    );

    useEffect(() => {
        let isMounted = true;

        const loadCollection = async () => {
            const mixed = !commonCollectionId && uniqueCollectionIds.length > 0;
            setIsMixedState(mixed);

            if (mixed || !commonCollectionId) {
                if (isMounted) {
                    setIsCollectionInvalid(false);
                    setCollectionData(null);
                }

                return;
            }

            const selectedCollection = collections.find((item) => item.id === commonCollectionId);
            const collection = selectedCollection || (await fetchCollectionById(commonCollectionId));

            if (!isMounted) {
                return;
            }

            if (!collection) {
                setIsCollectionInvalid(true);
                setCollectionData(null);
            } else {
                setIsCollectionInvalid(false);
                setCollectionData(collection);
            }
        };

        loadCollection();

        return () => {
            isMounted = false;
        };
    }, [collections, fetchCollectionById, commonCollectionId, uniqueCollectionIds.length]);

    return {
        containerRef,
        locked: allLocked,
        isLoading,
        collections,
        chosenAsset: collectionData,
        isCollectionInvalid,
        isMixedState,
        searchTerm,
        setIsOpen,
        handleRemoveClick,
        handleClick,
        setSearchTerm,
        supportedElementsCount: selectedElements.length,
        uniqueCollectionsCount: uniqueCollectionIds.length,
        activeCollectionIds: uniqueCollectionIds,
    };
};
